import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User, UserSettings } from "../../types/user";
import { updateDoc, doc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
};

interface UpdateUserSettingsPayload {
  userId: string;
  settings: Partial<UserSettings>;
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUserSettings: (state, action: PayloadAction<UpdateUserSettingsPayload>) => {
      if (state.user) {
        state.user.settings = {
          ...state.user.settings,
          ...action.payload.settings,
        };
      }
    },
  },
});

export const { setUser, setLoading, setError, clearError, updateUserSettings } = authSlice.actions;

export const updateUserSettingsThunk = 
  ({ userId, settings }: UpdateUserSettingsPayload) => 
  async (dispatch: any) => {
    try {
      dispatch(setLoading(true));
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { settings });
      dispatch(updateUserSettings({ userId, settings }));
      dispatch(setLoading(false));
    } catch (error) {
      dispatch(setError((error as Error).message));
    }
  };

export default authSlice.reducer;
